//#region types
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, DateRangeFormFieldProps } from "models/propTypes/genericFormProps";
import { GenericFormSection } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { TimeReportingFormTypes } from "models/formTypes";
import { FormikProps } from "formik";
//#endregion
//#region components
import { useBusinessUnitAutoComplete } from "components/businessUnit";
import { useCompanyService, useTimeReportingProjectService } from "serviceHooks";
//#endregion
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { DateHelper, QueryStateHelper } from "utils/helpers";

function useTimeReportingProjectProfileFilterDefinition(projectId: string, projectStartDate?: Date) {
  const timeReportingProjectService = useTimeReportingProjectService();
  const companyService = useCompanyService();

  const businessUnitAutoComplete = useBusinessUnitAutoComplete();

  const dateIntervalFieldName = FormFieldConstants.TimeReporting.Filter.DateInterval;
  const startFieldName = `${dateIntervalFieldName}.${FormFieldConstants.Util.DateRange.Start}`;
  const endFieldName = `${dateIntervalFieldName}.${FormFieldConstants.Util.DateRange.End}`;

  const onPeriodChange = (
    name: string,
    updatedValue: Date,
    formikProps: FormikProps<TimeReportingFormTypes.Filter>,
    userInput?: boolean | undefined
  ) => {
    const { setValues, values } = formikProps;

    if (updatedValue) {
      setValues({
        ...values,
        dateInterval: undefined,
        onlyIncludeHourlyIndividualBillingModel: values.dateInterval
          ? false
          : values.onlyIncludeHourlyIndividualBillingModel,
        period: updatedValue,
      });
    }
  };

  const onDateChange = (
    fieldName: string,
    updatedValue: Date,
    formikProps: FormikProps<TimeReportingFormTypes.Filter>
  ) => {
    const { setValues, values } = formikProps;
    const newValue = { ...values.dateInterval };

    if (fieldName === startFieldName) {
      newValue.start = updatedValue;
      if (updatedValue && !newValue.end && DateHelper.isBefore(updatedValue as Date, new Date())) {
        newValue.end = new Date();
      }
    } else if (fieldName === endFieldName) {
      newValue.end = updatedValue;
      if (updatedValue && !newValue.start) {
        newValue.start = projectStartDate && new Date(projectStartDate);
      }
    }
    setValues({ ...values, period: undefined, onlyIncludeHourlyIndividualBillingModel: true, dateInterval: newValue });
  };

  const filterFormFields: GenericFormSection<TimeReportingFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.Filter,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        categoryName: {
          label: TranslationKeys.TimeReportingProject.Category,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReporting.Filter.CategoryId,
            items: (searchStr: string) =>
              timeReportingProjectService.getProjectCategoriesAutoComplete(searchStr, projectId),
          } as AutoCompleteExtraProps<TimeReportingFormTypes.Filter>,
        },
        personName: {
          label: TranslationKeys.Common.Users,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WUser,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReporting.Filter.PersonId,
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          } as AutoCompleteExtraProps<TimeReportingFormTypes.Filter>,
        },
        businessUnitName: {
          label: TranslationKeys.BusinessUnit.BusinessUnitName,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WCustomer,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReporting.Filter.BusinessUnitId,
            items: (searchStr: string) => businessUnitAutoComplete(searchStr),
          } as AutoCompleteExtraProps<TimeReportingFormTypes.Filter>,
        },
        period: {
          label: TranslationKeys.TimeReporting.Month,
          type: FormEnums.FormFieldType.MONTH,
          onChangeHandler: onPeriodChange,
        },
        excludeInvoiced: {
          label: TranslationKeys.TimeReporting.ExcludeInvoiced,
          type: FormEnums.FormFieldType.TOGGLE,
        },
        dateInterval: {
          label: TranslationKeys.TimeReporting.DateInterval,
          descriptionText: TranslationKeys.TimeReporting.DateIntervalDescription,
          type: FormEnums.FormFieldType.DATERANGE,
          extraProps: {
            restrictCrossDateSelection: true,
            onChange: onDateChange,
          } as DateRangeFormFieldProps<TimeReportingFormTypes.Filter>,
        },
        onlyIncludeHourlyIndividualBillingModel: {
          label: TranslationKeys.TimeReporting.OnlyIncludeHourlyIndividualBillingModel,
          type: FormEnums.FormFieldType.TOGGLE,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<TimeReportingFormTypes.Filter> = {
    personId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.TimeReporting.Filter.PersonId),
    personName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.TimeReporting.Filter.PersonName),
    period: QueryStateHelper.monthQueryEncoder(FormFieldConstants.TimeReporting.Filter.Period),
    excludeInvoiced: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.TimeReporting.Filter.ExcludeInvoiced),
    businessUnitId: QueryStateHelper.stringQueryEncoder(FormFieldConstants.TimeReporting.Filter.BusinessUnitId),
    businessUnitName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.TimeReporting.Filter.BusinessUnitName),
    categoryId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.TimeReporting.Filter.CategoryId),
    categoryName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.TimeReporting.Filter.CategoryName),
    onlyIncludeHourlyIndividualBillingModel: QueryStateHelper.booleanQueryEncoder(
      FormFieldConstants.TimeReporting.Filter.OnlyIncludeHourlyIndividualBillingModel
    ),
    dateInterval: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.TimeReporting.Filter.DateInterval),
  };

  return { filterFormFields, filterQueryStateHandler };
}

export default useTimeReportingProjectProfileFilterDefinition;
